import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    Tooltip,
    Typography
} from '@mui/material';
import { ReactTable, Filter, InfoModal } from '@components';
import { MRT_ToggleGlobalFilterButton } from 'material-react-table';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { Buy } from './Buy';
import { Sell } from './Sell';
import { useDispatch, useSelector } from 'react-redux';
import {
    apiGetWatchlist,
    apiSaveToWatchlist,
    apiDeleteWatchlist,
    apiGetTtfChf,
    apiGetTtfMinMaxDisplay,
    apiGetTtfTrailingStopForDisplay,
    apiGetTtfDisableTrading
} from '@store';
import { showToast, GAS_PATH, isHighlighted } from '@utils';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Link, useNavigate } from 'react-router-dom';

const CHF = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.user);
    const {
        chf,
        minMaxDisplayChf: ttfMinMaxDisplay,
        trailingStopForDisplayChf: ttfTrailingStopForDisplay,
        disableTrading
    } = useSelector((state) => state.ttf || {});
    const {
        minMaxDisplayChf: globalMinMaxDisplay,
        trailingStopForDisplayChf: globalTrailingStopForDisplay
    } = useSelector((state) => state.the);
    const { limitOrder } = useSelector((state) => state.notification);

    const disableTradingStatus = disableTrading?.disable;
    const minMaxDisplay = ttfMinMaxDisplay;
    const trailingStopForDisplay = ttfTrailingStopForDisplay;

    // filter modal
    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleOpenFilterModal = () => setOpenFilterModal(true);
    const handleCloseFilterModal = () => setOpenFilterModal(false);

    // Load data on component mount
        useEffect(() => {
            dispatch(apiGetWatchlist());
        }, [dispatch]);

    const dataWithTS = useMemo(() => {
        const lastPrices = {};

        chf?.symbols?.forEach((symbol) => {
            let lastBidPrice = null;
            for (let i = limitOrder?.length - 1; i >= 0; i--) {
                if (
                    limitOrder[i]?.contract === symbol &&
                    limitOrder[i]?.type === 'bid' &&
                    limitOrder[i]?.market === 'TTF' &&
                    limitOrder[i]?.currency === 'CHF'
                ) {
                    lastBidPrice = limitOrder[i];
                    break;
                }
            }

            let lastAskPrice = null;
            for (let i = limitOrder?.length - 1; i >= 0; i--) {
                if (
                    limitOrder[i]?.contract === symbol &&
                    limitOrder[i]?.type === 'ask' &&
                    limitOrder[i]?.market === 'TTF' &&
                    limitOrder[i]?.currency === 'CHF'
                ) {
                    lastAskPrice = limitOrder[i];
                    break;
                }
            }

            lastPrices[symbol] = {
                lastBidPrice:
                    lastBidPrice?.price_gt || lastBidPrice?.price_lt || 0,
                lastAskPrice:
                    lastAskPrice?.price_gt || lastAskPrice?.price_lt || 0
            };
        });

        return chf?.symbols?.map((symbol) => {
            // Aggregate min/max values from the array
            const minMaxArray = minMaxDisplay?.[symbol] || [];
            const aggregatedMinMax = {
                maxbuy: 0,
                maxsell: 0,
                minbuy: 0,
                minsell: 0
            };

            minMaxArray.forEach((order) => {
                if (order.maxbuy > 0) aggregatedMinMax.maxbuy = order.maxbuy;
                if (order.maxsell > 0) aggregatedMinMax.maxsell = order.maxsell;
                if (order.minbuy > 0) aggregatedMinMax.minbuy = order.minbuy;
                if (order.minsell > 0) aggregatedMinMax.minsell = order.minsell;
            });

            return {
                contract: symbol,
                lastBidPrice: lastPrices[symbol]?.lastBidPrice,
                lastAskPrice: lastPrices[symbol]?.lastAskPrice,
                ...chf?.contracts[symbol],
                ...aggregatedMinMax,
                ...trailingStopForDisplay?.[symbol]
            };
        }) || [];
    }, [chf, limitOrder, minMaxDisplay, trailingStopForDisplay]);

    // Load TTF CHF data on component mount
    useEffect(() => {
        dispatch(apiGetTtfChf());
        dispatch(
            apiGetTtfMinMaxDisplay({ product: 'TTF', currency: 'CHF' })
        );
        dispatch(
            apiGetTtfTrailingStopForDisplay({
                product: 'TTF',
                currency: 'CHF'
            })
        );
        dispatch(apiGetTtfDisableTrading());
    }, [dispatch]);

    const [selectedContract, setSelectedContract] = useState(null);

    // info modal
    const [openInfoModal, setOpenInfoModal] = useState(false);
    const handleOpenInfoModal = () => {
        setOpenInfoModal(true);
    };
    const handleCloseInfoModal = () => {
        setOpenInfoModal(false);
    };

    // buy modal
    const [openBuyModal, setOpenBuyModal] = useState(false);
    const handleOpenBuyModal = useCallback((selectedContract) => {
        setOpenBuyModal(true);
        setSelectedContract(selectedContract);
    }, []);
    const handleCloseBuyModal = () => setOpenBuyModal(false);

    // sell modal
    const [openSellModal, setOpenSellModal] = useState(false);
    const handleOpenSellModal = useCallback((selectedContract) => {
        setSelectedContract(selectedContract);
        setOpenSellModal(true);
    }, []);
    const handleCloseSellModal = () => setOpenSellModal(false);

    // toggle watchlist
    const handleAddToWatchlist = useCallback(
        async (symbol, product, isInWatchlist) => {
            try {
                const response = isInWatchlist
                    ? await dispatch(
                          apiDeleteWatchlist({
                              symbol,
                              product,
                              currency: 'chf'
                          })
                      )
                    : await dispatch(
                          apiSaveToWatchlist({
                              symbol,
                              product,
                              currency: 'chf'
                          })
                      );
                if (response.meta.requestStatus === 'fulfilled') {
                    showToast(
                        'success',
                        isInWatchlist
                            ? 'Watchlist removed successfully'
                            : 'Added to watchlist'
                    );
                    dispatch(apiGetWatchlist());
                    dispatch(apiGetTtfChf());
                } else if (response.meta.requestStatus === 'rejected') {
                    showToast('error', response.error.message);
                }
            } catch (error) {
                showToast('error', error.message);
            }
        },
        [dispatch]
    );

       


    // go to details
    // DISABLED FOR CHF - Contract click should only be available on EUR
    // const handleGoToDetails = (contract) => {
    //     navigate(
    //         `${GAS_PATH.TTF_CONTRACT_DETAILS}?sym=${contract}&currency=chf&product=TTF`
    //     );
    // };

    const columns = useMemo(
        () => [
            {
                header: 'Contract',
                id: 'contract',
                accessorFn: ({ contract }) => contract || '',
                Cell: ({ renderedCellValue }) => (
                    <Typography
                        sx={{
                            color: '#3B9D9D',
                            fontSize: 'inherit',
                            fontWeight: 'bold',
                            pl: '8px'
                        }}
                    >
                        {renderedCellValue}
                    </Typography>
                ),
                size: 50
            },
            {
                header: 'Bid',
                id: 'bid',
                Cell: ({ row }) => (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '4px',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography>{row.original?.bid}</Typography>
                        <Typography
                            sx={{
                                opacity: 0.5,
                                fontSize: '14px',
                                marginLeft: '4px'
                            }}
                        >
                            {row?.original?.lastBidPrice === 0
                                ? ''
                                : row?.original?.lastBidPrice}
                        </Typography>
                    </Box>
                ),
                size: 50
            },
            {
                header: 'Ask',
                id: 'ask',
                Cell: ({ row }) => (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: '4px',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography>{row.original?.ask}</Typography>
                        <Typography
                            sx={{
                                opacity: 0.5,
                                fontSize: '14px'
                            }}
                        >
                            {row?.original?.lastAskPrice === 0
                                ? ''
                                : row?.original?.lastAskPrice}
                        </Typography>
                    </Box>
                ),
                size: 50
            },
            {
                header: 'Max',
                id: 'maxbuy',
                Cell: ({ row }) => {
                    const maxbuy = row?.original?.maxbuy;
                    const maxsell = row?.original?.maxsell;
                    const displayText = [
                        maxbuy ? `B:${maxbuy?.toFixed(2)}` : '',
                        maxsell ? `S:${maxsell?.toFixed(2)}` : ''
                    ].filter(Boolean).join('/');
                    
                    return (
                        <Box onClick={(e) => e.stopPropagation()}>
                            {displayText ? (
                                <Link
                                    to={GAS_PATH.NOTIFICATIONS}
                                    className="underline cursor-pointer text-sky-600"
                                >
                                    {displayText}
                                </Link>
                            ) : ''}
                        </Box>
                    );
                },
                size: 50
            },
            {
                header: 'Min',
                id: 'minbuy',
                Cell: ({ row }) => {
                    const minbuy = row?.original?.minbuy;
                    const minsell = row?.original?.minsell;
                    const displayText = [
                        minbuy ? `B:${minbuy?.toFixed(2)}` : '',
                        minsell ? `S:${minsell?.toFixed(2)}` : ''
                    ].filter(Boolean).join('/');
                    
                    return (
                        <Box onClick={(e) => e.stopPropagation()}>
                            {displayText ? (
                                <Link
                                    to={GAS_PATH.NOTIFICATIONS}
                                    className="underline cursor-pointer text-sky-600"
                                >
                                    {displayText}
                                </Link>
                            ) : ''}
                        </Box>
                    );
                },
                size: 50
            },
            {
                header: 'Ceiling',
                id: 'ceiling',
                Cell: ({ row }) => (
                    <Link
                        to={GAS_PATH.NOTIFICATIONS}
                        className="underline cursor-pointer text-sky-600"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {Number.isFinite(row?.original?.[0]?.ceiling)
                            ? row.original[0].ceiling.toFixed(2)
                            : ''}
                    </Link>
                ),
                size: 50
            },
            {
                header: 'Floor',
                id: 'floor',
                Cell: ({ row }) => (
                    <Link
                        to={GAS_PATH.NOTIFICATIONS}
                        className="underline cursor-pointer text-sky-600"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {Number.isFinite(row?.original?.[0]?.floor)
                            ? row.original[0].floor.toFixed(2)
                            : ''}
                    </Link>
                ),
                size: 50
            },
            {
                header: 'Updated At',
                id: 'updated_at_cet_str',
                accessorFn: ({ updated_at_cet_str }) =>
                    updated_at_cet_str || '',
                Cell: ({ renderedCellValue }) => <>{renderedCellValue}</>,
                size: 50
            }
        ],
        []
    );

    // start of buy sell restriction modal
    // convert user.StartTime and user.EndTime string to hh:mm
    const startTime = user?.StartTime?.split(':').slice(0, 2).join(':');
    const endTime = user?.EndTime?.split(':').slice(0, 2).join(':');
    const lunchStart = user?.LunchStart?.split(':').slice(0, 2).join(':');
    const lunchEnd = user?.LunchEnd?.split(':').slice(0, 2).join(':');

    // convert into berlin time
    // if current time is between startTime and endTime and not between lunchStart and lunchEnd then show modal
    var europeTime = new Date().toLocaleString('en-US', {
        timeZone: 'Europe/Berlin'
    });
    var time = new Date(europeTime);
    var hours = time.getHours();
    var minutes = time.getMinutes();
    // Format current time like startTime and endTime (hh:mm)
    var currentTime = `${hours.toString().padStart(2, '0')}.${minutes
        .toString()
        .padStart(2, '0')}`;
    // end of buy sell restriction modal

    const actionsMenu = useMemo(
        () =>
            ({ row }) =>
                [
                    <Box
                        className="flex gap-4 items-center justify-center"
                        key="buttons"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                    >
                        {disableTradingStatus === false &&
                            user?.HideBuyButton === false && (
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    sx={{
                                        p: 0,
                                        m: 0,
                                        borderColor: '1px solid #27A9FF',
                                        color: '#27A9FF',
                                        fontSize: '13px',
                                        '&:hover': {
                                            borderColor: '1px solid #27A9FF',
                                            color: 'white',
                                            backgroundColor: '#27A9FF'
                                        }
                                    }}
                                    disabled={
                                        row?.original?.bid > row?.original?.ask
                                    }
                                    onClick={() => {
                                        if (
                                            startTime <= currentTime &&
                                            currentTime <= endTime &&
                                            (currentTime < lunchStart ||
                                                currentTime > lunchEnd)
                                        ) {
                                            handleOpenBuyModal(row.original);
                                        } else {
                                            handleOpenInfoModal();
                                        }
                                    }}
                                >
                                    Buy
                                </Button>
                            )}
                        {disableTradingStatus === false &&
                            user?.HideSellButton === false && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{
                                        p: 0,
                                        m: 0,
                                        borderColor: '1px solid #EB6969',
                                        color: '#EB6969',
                                        fontSize: '13px',
                                        '&:hover': {
                                            borderColor: '1px solid #EB6969',
                                            color: 'white',
                                            backgroundColor: '#EB6969'
                                        }
                                    }}
                                    disabled={
                                        row?.original?.bid > row?.original?.ask
                                    }
                                    onClick={() => {
                                        if (
                                            startTime <= currentTime &&
                                            currentTime <= endTime &&
                                            (currentTime < lunchStart ||
                                                currentTime > lunchEnd)
                                        ) {
                                            handleOpenSellModal(row.original);
                                        } else {
                                            handleOpenInfoModal();
                                        }
                                    }}
                                >
                                    Sell
                                </Button>
                            )}
                        <Tooltip
                            title={
                                row.original.watchlist
                                    ? 'Remove from watchlist'
                                    : 'Add to watchlist'
                            }
                        >
                            <Button
                                variant="outlined"
                                sx={{
                                    color: '#245372',
                                    borderColor: '#245372',
                                    width: '64px',
                                    height: '24.75px',
                                    '&:hover': {
                                        color: 'white',
                                        backgroundColor: '#245372'
                                    }
                                }}
                                onClick={() =>
                                    handleAddToWatchlist(
                                        row.original.contract,
                                        'TTF',
                                        row.original.watchlist
                                    )
                                }
                            >
                                {row.original.watchlist ? (
                                    <StarIcon
                                        sx={{
                                            p: 0,
                                            m: 0,
                                            fontSize: '18px',
                                            color: '#FFC107'
                                        }}
                                    />
                                ) : (
                                    <StarBorderIcon
                                        sx={{
                                            p: 0,
                                            m: 0,
                                            fontSize: '18px'
                                        }}
                                    />
                                )}
                            </Button>
                        </Tooltip>
                    </Box>
                ],
        [
            disableTradingStatus,
            user,
            handleOpenBuyModal,
            handleOpenSellModal,
            handleAddToWatchlist
        ]
    );

    // filter
    const [filteredData, setFilteredData] = useState([]);

    const [filter, setFilter] = useState({
        typeOfContract: '',
        bidPrice: {
            min: '',
            max: ''
        },
        askPrice: {
            min: '',
            max: ''
        }
    });

    useEffect(() => {
        if (dataWithTS?.length > 0) {
            if (filter.typeOfContract && filter.typeOfContract === 'yearly') {
                setFilteredData(
                    dataWithTS?.filter((item) => item.contract.includes('CAL')) || []
                );
            } else if (
                filter.typeOfContract &&
                filter.typeOfContract === 'monthly'
            ) {
                const months = [
                    'JAN',
                    'FEB',
                    'MAR',
                    'APR',
                    'MAY',
                    'JUN',
                    'JUL',
                    'AUG',
                    'SEP',
                    'OCT',
                    'NOV',
                    'DEC'
                ];
                setFilteredData(
                    dataWithTS?.filter((item) =>
                        months.includes(item.contract.slice(0, 3))
                    ) || []
                );
            } else if (
                filter.typeOfContract &&
                filter.typeOfContract === 'quarterly'
            ) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.contract.includes('Q')) || []
                );
            } else if (
                filter.typeOfContract &&
                filter.typeOfContract === 'winter'
            ) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.contract.includes('WINTER')) ||
                        []
                );
            } else if (
                filter.typeOfContract &&
                filter.typeOfContract === 'summer'
            ) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.contract.includes('SUMMER')) ||
                        []
                );
            } else {
                setFilteredData(dataWithTS);
            }
        }
    }, [filter, dataWithTS]);

    useEffect(() => {
        if (dataWithTS?.length > 0) {
            if (filter.bidPrice.min && filter.bidPrice.max) {
                setFilteredData(
                    dataWithTS?.filter(
                        (item) =>
                            item.bid >= filter.bidPrice.min &&
                            item.bid <= filter.bidPrice.max
                    ) || []
                );
            } else if (filter.bidPrice.min) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.bid >= filter.bidPrice.min) ||
                        []
                );
            } else if (filter.bidPrice.max) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.bid <= filter.bidPrice.max) ||
                        []
                );
            } else {
                setFilteredData(dataWithTS);
            }
        }
    }, [filter.bidPrice, dataWithTS]);

    useEffect(() => {
        if (dataWithTS?.length > 0) {
            if (filter.askPrice.min && filter.askPrice.max) {
                setFilteredData(
                    dataWithTS?.filter(
                        (item) =>
                            item.ask >= filter.askPrice.min &&
                            item.ask <= filter.askPrice.max
                    ) || []
                );
            } else if (filter.askPrice.min) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.ask >= filter.askPrice.min) ||
                        []
                );
            } else if (filter.askPrice.max) {
                setFilteredData(
                    dataWithTS?.filter((item) => item.ask <= filter.askPrice.max) ||
                        []
                );
            } else {
                setFilteredData(dataWithTS);
            }
        }
    }, [filter.askPrice, dataWithTS]);

    return (
        <>
            {/* info modal */}
            <Modal open={openInfoModal} onClose={handleCloseInfoModal}>
                <InfoModal
                    handleClose={handleCloseInfoModal}
                    startTime={startTime}
                    endTime={endTime}
                    lunchStart={lunchStart}
                    lunchEnd={lunchEnd}
                />
            </Modal>
            {/* buy modal */}
            <Modal open={openBuyModal} onClose={handleCloseBuyModal}>
                <Buy
                    handleClose={handleCloseBuyModal}
                    open={openBuyModal}
                    chf={chf}
                    selectedContract={selectedContract}
                    user={user}
                />
            </Modal>
            {/* sell modal */}
            <Modal open={openSellModal} onClose={handleCloseSellModal}>
                <Sell
                    handleClose={handleCloseSellModal}
                    open={openSellModal}
                    chf={chf}
                    selectedContract={selectedContract}
                    user={user}
                />
            </Modal>
            {/* filter modal */}
            <Modal open={openFilterModal} onClose={handleCloseFilterModal}>
                <Filter
                    handleClose={handleCloseFilterModal}
                    open={openFilterModal}
                    filter={filter}
                    setFilter={setFilter}
                />
            </Modal>
            <ReactTable
                renderToolbarInternalActions={({ table }) => (
                    <div onMouseDown={(e) => e.stopPropagation()}>
                        <MRT_ToggleGlobalFilterButton table={table} />
                        <Tooltip title="Filter">
                            <IconButton onClick={handleOpenFilterModal}>
                                <FilterAltOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
                headerText={'TTF CHF'}
                columns={columns}
                data={filteredData || []}
                actions={actionsMenu}
                // DISABLED FOR CHF - Contract click should only be available on EUR
                // handleRowClick={(row) => handleGoToDetails(row.contract)}
                muiTableBodyRowProps={({ row }) => ({
                    sx: {
                        // cursor: 'pointer',  // DISABLED FOR CHF
                        backgroundColor:
                            isHighlighted(row) && '#ffea67 !important'
                    }
                })}
            />
        </>
    );
};

export default CHF;
